import React, { useState, useEffect } from 'react'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import { API_BASE, getImageUrl } from '../../utils/api'

interface Testimonial {
    _id: string;
    name: string;
    position: string;
    desc: string;
    image: string;
    quote: string;
}

export default function AdminTestimonials() {
    const { token } = useAuth()
    const [testimonials, setTestimonials] = useState<Testimonial[]>([])
    const [loading, setLoading] = useState(true)
    const [name, setName] = useState('')
    const [position, setPosition] = useState('')
    const [desc, setDesc] = useState('')
    const [image, setImage] = useState<File | null>(null)
    const [preview, setPreview] = useState('')
    const [quote, setQuote] = useState('bg-primary')
    const [submitting, setSubmitting] = useState(false)
    const [editId, setEditId] = useState<string | null>(null)
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null)
    const [showForm, setShowForm] = useState(false)

    useEffect(() => {
        fetchTestimonials()
    }, [])

    const fetchTestimonials = async () => {
        try {
            const res = await fetch(`${API_BASE}/testimonials`)
            const data = await res.json()
            if (Array.isArray(data)) setTestimonials(data)
        } catch (err) {
            console.error(err)
        } finally {
            setLoading(false)
        }
    }

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0]
            setImage(file)
            setPreview(URL.createObjectURL(file))
        }
    }

    const resetForm = () => {
        setName('')
        setPosition('')
        setDesc('')
        setImage(null)
        setPreview('')
        setQuote('bg-primary')
        setEditId(null)
        setShowForm(false)
    }

    const handleEdit = (t: Testimonial) => {
        setName(t.name)
        setPosition(t.position)
        setDesc(t.desc)
        setQuote(t.quote)
        setPreview(t.image)
        setEditId(t._id)
        setMsg(null)
        setShowForm(true)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!name || !position || !desc || !token) return
        setSubmitting(true)
        setMsg(null)

        let imageUrl = preview || '/client-1.png'

        try {
            if (image) {
                const fd = new FormData()
                fd.append('image', image)
                const uploadRes = await fetch(`${API_BASE}/upload/nearby-image`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                    body: fd
                })
                const uploadData = await uploadRes.json()
                if (uploadData.url) imageUrl = uploadData.url
            }

            const url = editId ? `${API_BASE}/testimonials/${editId}` : `${API_BASE}/testimonials`
            const method = editId ? 'PUT' : 'POST'

            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ name, position, desc, image: imageUrl, quote })
            })

            if (res.ok) {
                setMsg({ type: 'success', text: editId ? 'Testimonial updated successfully!' : 'Testimonial added successfully!' })
                resetForm()
                fetchTestimonials()
            } else {
                const errData = await res.json()
                setMsg({ type: 'error', text: errData.message || 'Failed to save testimonial' })
            }
        } catch (err) {
            setMsg({ type: 'error', text: 'Server error. Please try again.' })
        } finally {
            setSubmitting(false)
        }
    }

    const handleDelete = async (id: string) => {
        if (!token || !window.confirm('Are you sure you want to delete this testimonial?')) return
        try {
            const res = await fetch(`${API_BASE}/testimonials/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            })
            if (res.ok) {
                setTestimonials(prev => prev.filter(t => t._id !== id))
                setMsg({ type: 'success', text: 'Testimonial deleted.' })
                if (editId === id) resetForm()
            }
        } catch (err) {
            console.error(err)
        }
    }

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                <div>
                    <h1 className="text-3xl font-bold text-black mb-2 tracking-tight">Social Proof</h1>
                    <p className="text-black text-sm font-bold">Curate and manage authentic testimonials that build trust with your audience.</p>
                </div>
                <button 
                    onClick={() => { resetForm(); setShowForm(true); }}
                    className="px-8 py-4 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 rounded-md font-bold text-[14px] transition-all shadow-xl flex items-center gap-3"
                >
                    <i className="fa-solid fa-plus text-[12px]"></i> New Intake Session
                </button>
            </div>

            {msg && (
                <div className={`mb-10 p-5 rounded-md flex items-center gap-4 animate-in fade-in slide-in-from-top-4 border shadow-sm ${
                    msg.type === 'error' 
                    ? 'bg-rose-50 text-rose-700 border-rose-100' 
                    : 'bg-emerald-50 text-emerald-700 border-emerald-100'
                }`}>
                    <i className={`fa-solid ${msg.type === 'error' ? 'fa-triangle-exclamation' : 'fa-circle-check'} text-lg`}></i>
                    <p className="text-[14px] font-bold">{msg.text}</p>
                </div>
            )}

            <div className="bg-white dark:bg-slate-900 rounded-md border border-blue-900/10 dark:border-slate-800 shadow-2xl shadow-slate-200/50 dark:shadow-none overflow-hidden mb-12">
                <div className="p-8 border-b border-blue-900/10 dark:border-slate-800 flex items-center justify-between bg-white/50 dark:bg-slate-800/30">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-md bg-white border border-[#05144b] text-[#05144b] flex items-center justify-center shadow-lg">
                            <i className="fa-solid fa-quote-left text-lg"></i>
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-black">Active Testimonials</h3>
                            <p className="text-[12px] font-bold text-black/60">{testimonials.length} broadcast credentials</p>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="p-32 text-center">
                            <div className="animate-spin w-12 h-12 border-4 border-[#05144b] border-t-transparent rounded-full mx-auto mb-6"></div>
                            <p className="text-black font-bold text-[14px]">Syncing Credentials...</p>
                        </div>
                    ) : (
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-white/50 dark:bg-slate-800/30 border-b border-blue-900/10 dark:border-slate-800">
                                    <th className="px-8 py-5 text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Client Portfolio</th>
                                    <th className="px-8 py-5 text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100">Review Digest</th>
                                    <th className="px-8 py-5 text-[12px] md:text-[13px] font-bold text-black border-b border-gray-100 text-right">Operations</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {testimonials.map(t => (
                                    <tr key={t._id} className="group hover:bg-white/50 dark:hover:bg-slate-800/20 transition-all">
                                        <td className="px-8 py-7">
                                            <div className="flex items-center gap-6">
                                                <div className="relative flex-shrink-0">
                                                    <img src={getImageUrl(t.image)} alt={t.name} className="w-14 h-14 rounded-md object-cover border-4 border-white dark:border-slate-800 shadow-xl group-hover:scale-105 transition-transform duration-500" />
                                                    <div className={`absolute -bottom-2 -right-2 w-6 h-6 rounded-md ${t.quote || 'bg-blue-950'} border-4 border-white dark:border-slate-900 flex items-center justify-center text-white text-[8px]`}>
                                                        <i className="fa-solid fa-quote-right"></i>
                                                    </div>
                                                </div>
                                                <div>
                                                    <h4 className="font-bold text-black group-hover:text-black/60 transition-colors text-[17px] tracking-tight leading-tight">{t.name}</h4>
                                                    <p className="text-[12px] font-bold text-black/40 mt-1">{t.position}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-7">
                                            <div className="text-[14px] text-black leading-relaxed max-w-sm line-clamp-2 font-bold">
                                                "{t.desc}"
                                            </div>
                                        </td>
                                        <td className="px-8 py-7">
                                            <div className="flex items-center justify-end gap-3 transition-all">
                                                <button 
                                                    className="w-11 h-11 rounded-md bg-white dark:bg-slate-800 text-blue-950 dark:text-blue-950 hover:bg-blue-950 hover:text-white transition-all flex items-center justify-center shadow-sm border border-blue-900/10 dark:border-slate-700 font-black italic text-[11px]"
                                                    onClick={() => handleEdit(t)}
                                                    title="Modify review"
                                                >
                                                    <i className="fa-solid fa-pen-nib text-xs"></i>
                                                </button>
                                                <button 
                                                    className="w-11 h-11 rounded-md bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 hover:bg-rose-600 hover:text-white transition-all flex items-center justify-center shadow-sm border border-rose-100 dark:border-rose-900/30 font-black italic text-[11px]"
                                                    onClick={() => handleDelete(t._id)}
                                                    title="Purge session"
                                                >
                                                    <i className="fa-solid fa-trash-can text-xs"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {testimonials.length === 0 && (
                                    <tr>
                                        <td colSpan={3} className="px-8 py-32 text-center text-balance overflow-hidden">
                                            <div className="w-24 h-24 rounded-full bg-white dark:bg-slate-800/50 flex items-center justify-center mx-auto mb-8 shadow-inner border border-blue-900/10 dark:border-slate-800">
                                                <i className="fa-solid fa-comment-slash text-4xl text-blue-100"></i>
                                            </div>
                                            <h3 className="text-xl font-black text-blue-950 dark:text-white mb-2 italic tracking-tighter uppercase">No audience reception</h3>
                                            <p className="text-blue-950 dark:text-blue-950 text-sm max-w-xs mx-auto font-bold italic">Gather and publish your first client feedback to build community trust.</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Premium Dialog Overlay */}
            {showForm && (
                <div className="fixed inset-0 z-[1100] flex items-center justify-center px-4 overflow-hidden">
                    <div className="absolute inset-0 bg-blue-950/80 backdrop-blur-sm animate-in fade-in duration-300" onClick={resetForm}></div>
                    <div className="relative w-full max-w-xl bg-white dark:bg-slate-900 rounded-md border border-blue-900/10 dark:border-slate-800 shadow-2xl animate-in zoom-in-95 duration-300">
                        <div className="relative h-28 bg-[#05144b] flex flex-col justify-end p-6">
                            <button 
                                 onClick={resetForm}
                                 className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 text-white hover:bg-white/40 transition-colors flex items-center justify-center"
                            >
                                 <i className="fa-solid fa-xmark text-sm"></i>
                            </button>
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-md bg-white p-1 shadow-2xl">
                                    <div className="w-full h-full rounded-[4px] bg-white flex items-center justify-center overflow-hidden">
                                        {preview ? (
                                            <img src={getImageUrl(preview)} alt="Preview" className="w-full h-full object-cover" />
                                        ) : (
                                            <i className="fa-solid fa-user-plus text-xl text-black"></i>
                                        )}
                                    </div>
                                </div>
                                <div className="mb-1">
                                    <h2 className="text-lg font-bold text-white tracking-tight">{editId ? 'Modify Credentials' : 'New Intake Session'}</h2>
                                    <p className="text-white/60 text-[10px] font-bold tracking-widest uppercase">Client Broadcast Configuration</p>
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 px-8">
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-[11px] font-bold text-black uppercase mb-2">Client Narrative</label>
                                            <input 
                                                type="text" 
                                                className="w-full px-5 py-3 rounded-md bg-white border border-gray-100 focus:ring-2 focus:ring-black text-black transition-all placeholder:text-gray-300 font-bold text-[14px] shadow-sm" 
                                                placeholder="Full Name"
                                                value={name} 
                                                onChange={e => setName(e.target.value)} 
                                                required 
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-black text-blue-950 italic tracking-widest mb-1.5 uppercase">Visual identification</label>
                                            <div className="relative">
                                                <input 
                                                    type="file" 
                                                    className="absolute inset-0 opacity-0 cursor-pointer z-10" 
                                                    onChange={handleImageChange} 
                                                />
                                                <div className="w-full px-4 py-2.5 rounded-md bg-white dark:bg-slate-800 border border-blue-900/10 dark:border-slate-800 text-blue-900 font-black italic text-[11px] shadow-inner flex items-center gap-3">
                                                    <i className="fa-solid fa-camera"></i>
                                                    {image ? image.name : 'Update client headshot'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-[11px] font-bold text-black uppercase mb-2">Professional Status</label>
                                            <input 
                                                type="text" 
                                                className="w-full px-5 py-3 rounded-md bg-white border border-gray-100 focus:ring-2 focus:ring-black text-black transition-all placeholder:text-gray-300 font-bold text-[14px] shadow-sm" 
                                                placeholder="Designation / Org"
                                                value={position} 
                                                onChange={e => setPosition(e.target.value)} 
                                                required 
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-black text-blue-950 italic tracking-widest mb-1.5 uppercase">Visual accent</label>
                                            <select 
                                                className="w-full px-4 py-2.5 rounded-md bg-white dark:bg-slate-800 border border-blue-900/10 dark:border-slate-800 focus:ring-2 focus:ring-blue-950 text-blue-950 placeholder:text-blue-950/30 font-black italic text-[11px] shadow-inner appearance-none outline-none cursor-pointer" 
                                                value={quote} 
                                                onChange={e => setQuote(e.target.value)}
                                            >
                                                <option value="bg-blue-950">Accent: Corporate Blue</option>
                                                <option value="bg-emerald-600">Accent: Growth Green</option>
                                                <option value="bg-indigo-600">Accent: Royal Purple</option>
                                                <option value="bg-rose-600">Accent: Passion Red</option>
                                                <option value="bg-teal-600">Accent: Marine Teal</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-4 border-t border-gray-100">
                                    <label className="block text-[11px] font-bold text-black uppercase mb-2">Session Transmission</label>
                                    <textarea 
                                        className="w-full px-5 py-3 rounded-md bg-white border border-gray-100 focus:ring-2 focus:ring-black text-black transition-all placeholder:text-gray-300 font-bold text-[14px] min-h-[100px] shadow-sm leading-relaxed" 
                                        placeholder="Paste the review message here..."
                                        value={desc} 
                                        onChange={e => setDesc(e.target.value)} 
                                        required
                                    />
                                </div>
                            </div>

                            <div className="mt-6 flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                                <button 
                                    type="button"
                                    onClick={resetForm}
                                    className="px-6 py-3 rounded-md bg-white text-black font-bold text-[12px] hover:bg-gray-50 transition-all border border-gray-100"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    className={`px-8 py-3 rounded-md font-bold text-[14px] transition-all transform active:scale-95 shadow-xl flex items-center justify-center gap-2 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50`}
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <div className="w-4 h-4 border-2 border-[#05144b] border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                        <>
                                            <i className={`fa-solid ${editId ? 'fa-floppy-disk' : 'fa-paper-plane'} text-[12px]`}></i>
                                            <span>{editId ? 'Update Credentials' : 'Publish Review'}</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </AdminLayout>
    )
}


